import moment from 'moment'
import { uniq } from 'lodash'
import { httpHelper } from '@/utils'

const state = {
    calendarDays: [],
    isCalendarLoading: false,
    isCorrectionPending: false,
    isTrackPending: false,
    isTruckPending: false,
    isTrailersLoading: false,
    isTrucksLoading: false,
    isTripsLoading: false,
    isRevokePending: false,
    isBulkEditPending: false,
    isErrorAcknowledgementPending: false,
    isAllTrucksLoading: false,
    selectedDay: {},
    selectedTruck: null,
    selectedTrips: [],
    trailers: [],
    allTrailers: [],
    trips: [],
    trucks: [],
    allTrucks: [],
}

const getters = {
    preparedCalendarDays(state) {
        return state.calendarDays
            .map(item => ({
                date: item.date,
                dateFormatted: moment(item.date)
                    .format('ll')
                    .replace(/[\s\d]+$/, '')
                    .replace(/,\s*$/, ''),
                dayFormatted: moment(item.date).format('ddd'),
                hasMarker: item.needs_manual_correction,
                url: item.url,
            }))
            .reverse()
    },
    preparedTrips(state) {
        return state.trips
            .reduce((acc, item) => {
                switch (item.type) {
                    case 'REGULAR':
                        return item.legs?.length
                            ? [
                                  ...acc,
                                  ...item.legs.map(leg => ({
                                      ...leg,
                                      type: item.type,
                                  })),
                              ]
                            : acc
                    case 'TRACK':
                    case 'REFUSED':
                        return [...acc, item]
                    case 'JOURNEY':
                        return item.reason === 'NO_JOURNEY'
                            ? [...acc, item]
                            : acc
                    default:
                        return acc
                }
            }, [])
            .map(item => {
                return {
                    ...item,
                    sortingDate:
                        item.type === 'TRACK' || item.type === 'JOURNEY'
                            ? item.correction_begin
                            : item.start,
                }
            })
            .sort((a, b) => {
                return new Date(a.sortingDate) - new Date(b.sortingDate)
            })
    },
    tripCorrections(state) {
        return state.trips.filter(
            item => item.type === 'TRAILER' || item.type === 'JOURNEY'
        )
    },
    allCorrections(state) {
        return state.trips.filter(
            item =>
                item.type === 'TRAILER' ||
                item.type === 'JOURNEY' ||
                item.type === 'TRACK'
        )
    },
    hasNoJourney(state) {
        return state.trips.some(
            item => item.type === 'JOURNEY' && item.reason === 'NO_JOURNEY'
        )
    },
    notRegisteredTrucks(state) {
        return state.allTrucks.filter(
            truck => truck.registration_status !== 'REGISTRATION_CONFIRMED'
        )
    },
    gpsGaps(state, getters) {
        const gaps = []
        const day = moment(
            state.selectedDay?.date || getters.preparedTrips[0]?.sortingDate
        )
        const trips = getters.preparedTrips
        if (trips[0]?.gps_gap_before) {
            gaps.push({
                type: 'GPS_GAP',
                start: day.startOf('day').toISOString(),
                sortingDate: day.startOf('day').toISOString(),
                end: moment(trips[0].start || trips[0].correction_begin)
                    .subtract(1, 'seconds')
                    .toISOString(),
            })
        }
        trips.forEach((trip, idx, trips) => {
            if (trip.gps_gap_after) {
                const start = moment(trip.end)
                    .add(1, 'seconds')
                    .toISOString()
                const end =
                    idx === trips.length - 1
                        ? day.endOf('day').toISOString()
                        : moment(
                              trips[idx + 1].start ||
                                  trips[idx + 1].correction_begin
                          )
                              .subtract(1, 'seconds')
                              .toISOString()
                gaps.push({
                    type: 'GPS_GAP',
                    start,
                    sortingDate: start,
                    end,
                })
            }
        })
        return gaps.filter(gap => {
            const gapStart = moment(gap.start)
            const gapEnd = moment(gap.end)
            return gapEnd.diff(gapStart, 'seconds') > 60
        })
    },
}

const actions = {
    async loadCalendarDays({ commit }) {
        commit('setCalendarLoading', true)
        commit('setCalendarDays', [])

        const { data } = await httpHelper.get(
            'lsva/regular-toll-declaration-calendar/'
        )

        commit('setCalendarDays', Object.freeze(data))
        commit('setCalendarLoading', false)
    },
    async fetchAllTrucks({ commit }) {
        commit('setAllTrucks', [])
        commit('setAllTrucksLoading', true)

        const { data } = await httpHelper.getRecursively('lsva/trucks/')

        commit('setAllTrucks', Object.freeze(data.results))
        commit('setAllTrucksLoading', false)
    },
    async loadTrucks({ commit, state }) {
        if (!state.selectedDay?.url) {
            return
        }

        commit('setTrucksLoading', true)
        commit('setTrucks', [])
        commit('setSelectedTruck', null)

        const { data } = await httpHelper.get(
            `${state.selectedDay.url}&limit=${process.env.VUE_APP_LIMIT_RECORDS_PER_REQUEST}`
        )

        commit('setTrucks', Object.freeze(uniq(data.results, 'id')))
        commit('setTrucksLoading', false)
    },
    async loadTrips({ commit, state }) {
        commit('setTripsLoading', true)
        commit('setTrips', [])

        const queryParams = new URLSearchParams()
        const selectedTruckId = state.selectedTruck?.toll_declaration.truck.id
        const selectedDate = state.selectedDay?.date

        if (selectedTruckId) {
            queryParams.append('truck', selectedTruckId)
        }

        if (selectedDate) {
            queryParams.append('declaration_period', selectedDate)
        }

        const url = `lsva/declarations/?${queryParams.toString()}`

        const { data } = await httpHelper.get(url)

        // Check if this request is the most recent one
        if (
            state.selectedTruck?.toll_declaration.truck.id !==
                selectedTruckId ||
            state.selectedDay?.date !== selectedDate
        ) {
            return
        }
        commit('setTrips', Object.freeze(data?.results ?? []))
        commit('setTripsLoading', false)
    },
    async loadTrailers({ commit }) {
        commit('setTrailers', [])
        commit('setTrailersLoading', true)

        const queryParams = new URLSearchParams()

        if (state.selectedTruck?.toll_declaration?.truck.id) {
            queryParams.append(
                'truck',
                state.selectedTruck.toll_declaration.truck.id
            )
        }

        const { data } = await httpHelper.getRecursively(
            `lsva/trailers/?${queryParams.toString()}`
        )

        commit('setTrailers', Object.freeze(data.results))
        commit('setTrailersLoading', false)
    },
    async loadAllTrailers({ commit }) {
        commit('setAllTrailers', [])

        const { data } = await httpHelper.getRecursively('lsva/trailers/')

        commit('setAllTrailers', Object.freeze(data.results))
    },
    async createCorrection({ commit, dispatch, state }, data) {
        commit('setCorrectionPending', true)

        try {
            const response =
                data.type === 'TRAILER'
                    ? await httpHelper.post(
                          'lsva/manual-toll-declarations/trailer/',
                          {
                              declaration_period: state.selectedDay.date,
                              correction_begin: `${state.selectedDay.date}T${data.time[0]}`,
                              correction_end: `${state.selectedDay.date}T${data.time[1]}`,
                              trailer:
                                  data.trailer?.id !== 'NO_TRAILER' &&
                                  data.trailer?.id !== 'OTHER_TRAILER'
                                      ? data.trailer?.id
                                      : null,
                              trailer_weight: Math.round(data.weight * 1000), // Convert to kilograms
                              trailer_type: data.trailerType,
                              truck:
                                  state.selectedTruck.toll_declaration.truck.id,
                              comment: data.comment,
                          }
                      )
                    : await httpHelper.post(
                          'lsva/manual-toll-declarations/journey/',
                          {
                              declaration_period: state.selectedDay.date,
                              correction_begin: `${state.selectedDay.date}T${data.time[0]}`,
                              correction_end: `${state.selectedDay.date}T${data.time[1]}`,
                              truck:
                                  state.selectedTruck.toll_declaration.truck.id,
                              reason: data.reason,
                              comment: data.comment,
                          }
                      )

            dispatch('loadTrips')
            return Promise.resolve(response)
        } catch (error) {
            return Promise.reject(error)
        } finally {
            commit('setCorrectionPending', false)
        }
    },
    async createTrack({ commit, dispatch, state }, data) {
        commit('setTrackPending', true)

        try {
            const response = await httpHelper.post(
                'lsva/manual-toll-declarations/track/',
                {
                    declaration_period: state.selectedDay.date,
                    correction_begin: `${state.selectedDay.date}T${data.time[0]}`,
                    correction_end: `${state.selectedDay.date}T${data.time[1]}`,
                    trailer:
                        data.trailer?.id !== 'NO_TRAILER' &&
                        data.trailer?.id !== 'OTHER_TRAILER'
                            ? data.trailer?.id
                            : null,
                    truck: state.selectedTruck.toll_declaration.truck.id,
                    trailer_weight: Math.round(data.weight * 1000), // Convert to kilograms
                    trailer_type: data.trailerType,
                    distance: Math.round(data.distance * 1000), // Convert to meters
                    reason: data.message,
                    comment: data.comment,
                }
            )

            dispatch('loadTrips')
            return Promise.resolve(response)
        } catch (error) {
            return Promise.reject(error)
        } finally {
            commit('setTrackPending', false)
        }
    },
    async updateTruck({ commit, dispatch, state }, data) {
        commit('setTruckPending', true)

        const selectedTruckId = state.selectedTruck?.toll_declaration.truck.id
        const registrationEndDate = moment(data.end_date).format('YYYY-MM-DD')
        const requests = []

        const body = {
            declaration_period: state.selectedDay.date,
            truck: state.selectedTruck.toll_declaration.truck.id,
        }

        if (data.vin) {
            body.old_vin = state.selectedTruck.toll_declaration.truck.vin
            body.replacement_vin = data.vin.replace(/\s+/g, '')
            requests.push(
                httpHelper.post('lsva/manual-toll-declarations/vin/', body)
            )
        }

        if (data.end_date && registrationEndDate) {
            requests.push(
                httpHelper.post(
                    `lsva/truck-registration-end/${state.selectedTruck.toll_declaration.truck.id}/`,
                    {
                        date: registrationEndDate,
                    }
                )
            )
        }

        try {
            const response = await Promise.all(requests)

            await dispatch('loadTrucks')
            commit(
                'setSelectedTruck',
                state.trucks.find(
                    truck => truck.toll_declaration.truck.id === selectedTruckId
                )
            )

            return Promise.resolve(response)
        } catch (error) {
            return Promise.reject(error)
        } finally {
            commit('setTruckPending', false)
        }
    },
    async revokeDeclaration({ commit, dispatch }, data) {
        commit('setRevokePending', true)

        try {
            const response = await httpHelper.post(
                'lsva/manual-toll-declarations/revocation/',
                {
                    declaration_period:
                        data.toll_declaration.declaration_period,
                    revoked_declaration:
                        data.toll_declaration.toll_declaration_id,
                }
            )

            dispatch('loadTrips')

            return Promise.resolve(response)
        } catch (error) {
            return Promise.reject(error)
        } finally {
            commit('setRevokePending', false)
        }
    },
    async revokeSelectedDeclarations({ commit, dispatch, state }) {
        commit('setRevokePending', true)

        const requests = state.selectedTrips
            .filter(
                item =>
                    item.toll_declaration?.declaration_period &&
                    item.toll_declaration?.toll_declaration_id
            )
            .map(item =>
                httpHelper.post('lsva/manual-toll-declarations/revocation/', {
                    declaration_period:
                        item.toll_declaration.declaration_period,
                    revoked_declaration:
                        item.toll_declaration.toll_declaration_id,
                })
            )

        try {
            const response = await Promise.all(requests)

            dispatch('loadTrips')

            return Promise.resolve(response)
        } catch (error) {
            return Promise.reject(error)
        } finally {
            commit('setRevokePending', false)
        }
    },
    async sendErrorAcknowledgement({ commit, dispatch, state }) {
        commit('setErrorAcknowledgementPending', true)

        const selectedTruckId = state.selectedTruck?.toll_declaration.truck.id

        try {
            const id = state.selectedTruck.id
            const response = await httpHelper.patch(
                `lsva/regular-toll-declarations/${id}`,
                {
                    needs_manual_correction: false,
                }
            )

            await dispatch('loadTrucks')
            commit(
                'setSelectedTruck',
                state.trucks.find(
                    truck => truck.toll_declaration.truck.id === selectedTruckId
                )
            )

            return Promise.resolve(response)
        } catch (error) {
            return Promise.reject(error)
        } finally {
            commit('setErrorAcknowledgementPending', false)
        }
    },
    setSelectedDay({ commit, dispatch }, data) {
        commit('setSelectedDay', data)
        dispatch('loadTrucks')
    },
    setSelectedDayByDate({ commit, dispatch, state }, date) {
        const foundDay = state.calendarDays.find(item => item.date === date)
        if (!foundDay) return

        commit('setSelectedDay', foundDay)
        dispatch('loadTrucks')
    },
    setSelectedTruck({ commit, dispatch }, data) {
        commit('setSelectedTruck', data)
        dispatch('loadTrips')
    },
    setSelectedTruckById({ commit, dispatch, state }, id) {
        const foundTruck = state.trucks.find(
            truck => truck.toll_declaration.truck.id === Number(id)
        )
        if (!foundTruck) return

        commit('setSelectedTruck', foundTruck)
        dispatch('loadTrips')
    },
    selectTrip({ commit }, trip) {
        commit('selectTrip', trip)
    },
    selectAllTrips({ commit }, trips) {
        commit('setSelectedTrips', trips)
    },
    async bulkEditTrips({ commit, state, dispatch }, data) {
        commit('setBulkEditPending', true)

        try {
            const response = await Promise.all(
                state.selectedTrips.reduce((acc, trip) => {
                    if (
                        trip.type === 'REGULAR' &&
                        trip.trailer?.id !== data.trailer?.id
                    ) {
                        acc.push(
                            httpHelper.post(
                                'lsva/manual-toll-declarations/trailer/',
                                {
                                    declaration_period: state.selectedDay.date,
                                    correction_begin: trip.start,
                                    correction_end: trip.end,
                                    trailer:
                                        data.trailer?.id !== 'NO_TRAILER' &&
                                        data.trailer?.id !== 'OTHER_TRAILER'
                                            ? data.trailer?.id
                                            : null,
                                    trailer_weight: Math.round(
                                        data.weight * 1000
                                    ), // Convert to kilograms
                                    trailer_type: data.trailerType,
                                    truck:
                                        state.selectedTruck.toll_declaration
                                            .truck.id,
                                    comment: data.comment,
                                }
                            )
                        )
                    }
                    return acc
                }, [])
            )

            return Promise.resolve(response)
        } catch (error) {
            return Promise.reject(error)
        } finally {
            dispatch('loadTrips')
            commit('setSelectedTrips', [])
            commit('setBulkEditPending', false)
        }
    },
}
const mutations = {
    selectTrip(state, trip) {
        const index = state.selectedTrips.findIndex(item => item === trip)
        if (index !== -1) {
            state.selectedTrips.splice(index, 1)
        } else {
            state.selectedTrips.push(trip)
        }
    },
    setSelectedTrips(state, trips) {
        state.selectedTrips = [...trips]
    },
    setCalendarLoading(state, data) {
        state.isCalendarLoading = data
    },
    setCalendarDays(state, data) {
        state.calendarDays = data
    },
    setTrucksLoading(state, data) {
        state.isTrucksLoading = data
    },
    setTrucks(state, data) {
        state.trucks = data
    },
    setSelectedDay(state, data) {
        state.selectedDay = data
    },
    setSelectedTruck(state, data) {
        state.selectedTruck = data
    },
    setTrips(state, data) {
        state.trips = data
    },
    setTripsLoading(state, data) {
        state.isTripsLoading = data
    },
    setBulkEditPending(state, data) {
        state.isBulkEditPending = data
    },
    setTrailers(state, data) {
        state.trailers = data
    },
    setAllTrailers(state, data) {
        state.allTrailers = data
    },
    setTrailersLoading(state, data) {
        state.isTrailersLoading = data
    },
    setCorrectionPending(state, data) {
        state.isCorrectionPending = data
    },
    setTrackPending(state, data) {
        state.isTrackPending = data
    },
    setTruckPending(state, data) {
        state.isTruckPending = data
    },
    setRevokePending(state, data) {
        state.isRevokePending = data
    },
    setErrorAcknowledgementPending(state, data) {
        state.isErrorAcknowledgementPending = data
    },
    setAllTrucks(state, data) {
        state.allTrucks = data
    },
    setAllTrucksLoading(state, data) {
        state.isAllTrucksLoading = data
    },
}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
}
